import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, Shield, Edit, AlertCircle, CheckCircle } from 'lucide-react';
import { apiService, RateLimitDefaults, UpdateRateLimitDefaultsRequest } from '../../services/api';
import { toast } from 'sonner';

export function RateLimitSettingsPage() {
  const [config, setConfig] = useState<RateLimitDefaults | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<UpdateRateLimitDefaultsRequest>({
    messageRateLimit: 30,
    sessionCreationLimit: 3,
    concurrentSessionLimit: 10,
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getRateLimitDefaults();
      setConfig(data);
      setFormData({
        messageRateLimit: data.messageRateLimit,
        sessionCreationLimit: data.sessionCreationLimit,
        concurrentSessionLimit: data.concurrentSessionLimit,
      });
    } catch (err: any) {
      console.error('Failed to load rate-limit config:', err);
      setError('Rate-Limit-Einstellungen konnten nicht geladen werden');
      toast.error('Einstellungen konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await apiService.updateRateLimitDefaults(formData);
      toast.success('Rate-Limit-Einstellungen gespeichert');
      await loadConfig();
      setIsDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to save rate-limit config:', err);
      toast.error(err.message || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const openEditDialog = () => {
    if (config) {
      setFormData({
        messageRateLimit: config.messageRateLimit,
        sessionCreationLimit: config.sessionCreationLimit,
        concurrentSessionLimit: config.concurrentSessionLimit,
      });
    }
    setIsDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Lade Einstellungen...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Rate Limiting
          </h1>
          <p className="text-gray-600">Konfigurieren Sie die globalen Spam-Schutz-Einstellungen</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadConfig}>Erneut versuchen</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Rate Limiting
          </h1>
          <p className="text-gray-600">
            Globale Spam-Schutz-Einstellungen fuer alle Chatbot-Agenten
          </p>
        </div>
        <Button onClick={openEditDialog}>
          <Edit className="w-4 h-4 mr-2" />
          Bearbeiten
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Globale Standard-Limits</h3>
              <p className="text-sm text-blue-700 mt-1">
                Diese Werte gelten als Standard fuer alle Chatbot-Agenten, sofern kein individuelles
                Rate-Limit in der Agent-Konfiguration hinterlegt ist. Agenten-spezifische Einstellungen
                haben Vorrang vor diesen globalen Defaults.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Aktuelle Konfiguration</CardTitle>
              <CardDescription>
                Diese Einstellungen gelten global als Fallback fuer alle Agenten
              </CardDescription>
            </div>
            <Badge variant="default">
              <CheckCircle className="w-3 h-3 mr-1" />
              Aktiv
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-500">Nachrichten pro Minute</Label>
                <p className="text-lg font-medium">{config?.messageRateLimit}</p>
                <p className="text-xs text-gray-400">Max. Nachrichten pro Minute pro Session</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">Sessions pro Minute</Label>
                <p className="text-lg font-medium">{config?.sessionCreationLimit}</p>
                <p className="text-xs text-gray-400">Max. neue Sessions pro Minute pro IP</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-500">Gleichzeitige Sessions</Label>
                <p className="text-lg font-medium">{config?.concurrentSessionLimit}</p>
                <p className="text-xs text-gray-400">Max. aktive Sessions pro IP</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Rate-Limit-Einstellungen bearbeiten</DialogTitle>
            <DialogDescription>
              Passen Sie die globalen Spam-Schutz-Limits an
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Message Rate Limit */}
            <div className="space-y-2">
              <Label htmlFor="messageRateLimit">Nachrichten pro Minute</Label>
              <Input
                id="messageRateLimit"
                type="number"
                min="1"
                max="1000"
                value={formData.messageRateLimit}
                onChange={(e) => setFormData({ ...formData, messageRateLimit: parseInt(e.target.value) || 30 })}
              />
              <p className="text-xs text-gray-500">
                Maximale Anzahl an Nachrichten pro Minute pro Chat-Session (1-1000)
              </p>
            </div>

            {/* Session Creation Limit */}
            <div className="space-y-2">
              <Label htmlFor="sessionCreationLimit">Sessions pro Minute</Label>
              <Input
                id="sessionCreationLimit"
                type="number"
                min="1"
                max="100"
                value={formData.sessionCreationLimit}
                onChange={(e) => setFormData({ ...formData, sessionCreationLimit: parseInt(e.target.value) || 3 })}
              />
              <p className="text-xs text-gray-500">
                Maximale Anzahl neuer Chat-Sessions pro Minute pro IP-Adresse (1-100)
              </p>
            </div>

            {/* Concurrent Session Limit */}
            <div className="space-y-2">
              <Label htmlFor="concurrentSessionLimit">Gleichzeitige Sessions</Label>
              <Input
                id="concurrentSessionLimit"
                type="number"
                min="1"
                max="500"
                value={formData.concurrentSessionLimit}
                onChange={(e) => setFormData({ ...formData, concurrentSessionLimit: parseInt(e.target.value) || 10 })}
              />
              <p className="text-xs text-gray-500">
                Maximale Anzahl gleichzeitig aktiver Sessions pro IP-Adresse (1-500)
              </p>
            </div>

          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
